import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Calendar, User, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";
import { SharedHeader } from "@/components/SharedHeader";

// Blog images
import aiAnsweringImg from "@/assets/blog/ai-answering-service.jpg";
import aiVsHumanImg from "@/assets/blog/ai-vs-human-cost.jpg";
import aiReceptionistImg from "@/assets/blog/ai-receptionist-small-business.jpg";
import reduceCostsImg from "@/assets/blog/reduce-support-costs.jpg";
import aiRealEstateImg from "@/assets/blog/ai-real-estate.jpg";
import aiPhoneSupportImg from "@/assets/blog/ai-phone-support.jpg";
import aiMedicalImg from "@/assets/blog/ai-medical-clinic.jpg";
import aiWhatsappImg from "@/assets/blog/ai-whatsapp-integration.jpg";
import aiRestaurantsImg from "@/assets/blog/ai-restaurants.jpg";
import bestAiSoftwareImg from "@/assets/blog/best-ai-software.jpg";
import aiCallCenterImg from "@/assets/blog/ai-call-center-setup.jpg";
import voiceVsChatbotImg from "@/assets/blog/voice-vs-chatbot.jpg";
import futureAiImg from "@/assets/blog/future-ai-service.jpg";
import chatgptMcpImg from "@/assets/blog/chatgpt-mcp-phone-call.jpg";
import aiVoiceCloningImg from "@/assets/blog/ai-voice-cloning-phone-agent.jpg";
import openclawPhoneImg from "@/assets/blog/openclaw-phone-calls.jpg";

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  date: string;
  author: string;
  image: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: "openclaw-phone-calls",
    title: "How to Add Phone Calls to OpenClaw Easily with Call24x7",
    description: "Learn how to give your OpenClaw AI agent the power of real phone calls using Call24x7.AI. Make and receive calls autonomously in minutes.",
    category: "Integration",
    readTime: "8 min read",
    date: "February 28, 2026",
    author: "Call24x7 Team",
    image: openclawPhoneImg
  },
  {
    slug: "ai-voice-cloning-phone-agent",
    title: "AI Phone Agent with Your Voice: Clone Your Voice in 30 Seconds",
    description: "Create an AI phone agent that sounds exactly like you. Simply upload an MP3 or record 30 seconds with your microphone - that's all it takes with Call24x7.AI.",
    category: "Voice Cloning",
    readTime: "7 min read",
    date: "December 21, 2025",
    author: "Call24x7 Team",
    image: aiVoiceCloningImg
  },
  {
    slug: "chatgpt-mcp-phone-calls",
    title: "Make Phone Calls from ChatGPT with Call24x7.AI MCP Integration",
    description: "Revolutionary: Learn how to make real phone calls directly from ChatGPT using Call24x7.AI's MCP integration. The future of AI-powered communication is here.",
    category: "Innovation",
    readTime: "8 min read",
    date: "December 15, 2025",
    author: "Call24x7 Team",
    image: chatgptMcpImg
  },
  {
    slug: "what-is-ai-answering-service",
    title: "What Is an AI Answering Service and How Does It Work?",
    description: "Discover how AI answering services revolutionize customer communication with 24/7 availability, natural language processing, and seamless call handling.",
    category: "AI Basics",
    readTime: "5 min read",
    date: "December 9, 2025",
    author: "Call24x7 Team",
    image: aiAnsweringImg
  },
  {
    slug: "ai-vs-human-agents-cost-comparison",
    title: "AI vs Human Agents: Cost Comparison for Small Businesses",
    description: "A detailed breakdown of costs between AI customer support and traditional human agents. See how small businesses can save up to 70% with AI.",
    category: "Cost Analysis",
    readTime: "7 min read",
    date: "November 12, 2025",
    author: "Call24x7 Team",
    image: aiVsHumanImg
  },
  {
    slug: "ai-receptionist-for-small-business",
    title: "AI Receptionist for Small Business: Complete Guide 2025",
    description: "Learn how AI receptionists help small businesses handle calls 24/7, reduce costs by 60%, and never miss a customer inquiry.",
    category: "Small Business",
    readTime: "6 min read",
    date: "November 5, 2025",
    author: "Call24x7 Team",
    image: aiReceptionistImg
  },
  {
    slug: "reduce-customer-support-costs-with-ai",
    title: "How AI Can Reduce Customer Support Costs by 70%",
    description: "Learn the strategies and tools that help businesses dramatically cut support costs while improving customer satisfaction with AI automation.",
    category: "ROI & Savings",
    readTime: "6 min read",
    date: "October 15, 2025",
    author: "Call24x7 Team",
    image: reduceCostsImg
  },
  {
    slug: "ai-for-real-estate-agents",
    title: "AI Phone Agents for Real Estate: Capture Every Lead",
    description: "How real estate agents use AI phone agents to qualify leads 24/7, schedule showings automatically, and never miss a buyer inquiry.",
    category: "Real Estate",
    readTime: "7 min read",
    date: "October 2, 2025",
    author: "Call24x7 Team",
    image: aiRealEstateImg
  },
  {
    slug: "top-ways-businesses-use-ai-phone-support",
    title: "Top 5 Ways Businesses Use AI for Phone Support",
    description: "Explore the most popular use cases for AI phone agents: from appointment scheduling to lead qualification and payment processing.",
    category: "Use Cases",
    readTime: "5 min read",
    date: "September 8, 2025",
    author: "Call24x7 Team",
    image: aiPhoneSupportImg
  },
  {
    slug: "ai-for-medical-clinics",
    title: "AI Phone Agents for Medical Clinics & Healthcare",
    description: "HIPAA-compliant AI phone agents for medical clinics. Automate appointment scheduling, prescription refills, and patient inquiries.",
    category: "Healthcare",
    readTime: "8 min read",
    date: "August 28, 2025",
    author: "Call24x7 Team",
    image: aiMedicalImg
  },
  {
    slug: "integrate-ai-customer-support-whatsapp",
    title: "How to Integrate AI Customer Support With WhatsApp & Webchat",
    description: "Step-by-step guide to extending your AI customer support platform across multiple channels including WhatsApp, webchat, and SMS.",
    category: "Integration",
    readTime: "8 min read",
    date: "August 1, 2025",
    author: "Call24x7 Team",
    image: aiWhatsappImg
  },
  {
    slug: "ai-for-restaurants",
    title: "AI Phone Agents for Restaurants: Handle Reservations 24/7",
    description: "How restaurants use AI phone agents to handle reservations, takeout orders, and customer inquiries. Increase bookings by 45%.",
    category: "Restaurants",
    readTime: "6 min read",
    date: "July 25, 2025",
    author: "Call24x7 Team",
    image: aiRestaurantsImg
  },
  {
    slug: "best-ai-phone-agent-software",
    title: "Best AI Phone Agent Software in 2025: Top 7 Solutions Compared",
    description: "Compare the top AI phone agent software solutions for 2025. Features, pricing, pros and cons of each platform.",
    category: "Software Review",
    readTime: "10 min read",
    date: "June 20, 2025",
    author: "Call24x7 Team",
    image: bestAiSoftwareImg
  },
  {
    slug: "how-to-set-up-ai-call-center",
    title: "How to Set Up an AI Call Center in 2025: Step-by-Step Guide",
    description: "Complete guide to setting up an AI call center. From choosing software to training your AI and measuring success.",
    category: "Implementation",
    readTime: "12 min read",
    date: "May 15, 2025",
    author: "Call24x7 Team",
    image: aiCallCenterImg
  },
  {
    slug: "ai-voice-agent-vs-chatbot",
    title: "AI Voice Agent vs Chatbot: Which Is Better for Your Business?",
    description: "AI voice agents vs chatbots: detailed comparison of capabilities, use cases, costs, and customer preferences.",
    category: "Comparison",
    readTime: "7 min read",
    date: "April 10, 2025",
    author: "Call24x7 Team",
    image: voiceVsChatbotImg
  },
  {
    slug: "future-of-ai-customer-service",
    title: "The Future of AI Customer Service: 2025-2030 Predictions",
    description: "Expert predictions for AI customer service from 2025 to 2030. Emerging technologies, industry trends, and how to prepare.",
    category: "Industry Trends",
    readTime: "9 min read",
    date: "March 5, 2025",
    author: "Call24x7 Team",
    image: futureAiImg
  }
];

const Blog = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  const { data: dynamicPosts } = useQuery({
    queryKey: ["dynamic-blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("slug, category, read_time, date, author, image_url, translations")
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const dynamicBlogCards: BlogPost[] = (dynamicPosts || []).map((p: any) => {
    const trans = p.translations as Record<string, { title: string; description: string }>;
    const t = trans[currentLang] || trans["en"] || { title: p.slug, description: "" };
    return {
      slug: p.slug,
      title: t.title,
      description: t.description,
      category: p.category,
      readTime: p.read_time,
      date: p.date,
      author: p.author,
      image: p.image_url || "/placeholder.svg",
    };
  });

  const allPosts = [...dynamicBlogCards, ...blogPosts];

  const POSTS_PER_PAGE = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(allPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = allPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>AI Customer Support Blog | Tips & Guides | Call24x7.AI</title>
        <meta name="description" content="Expert insights on AI customer support, AI answering services, cost savings, and automation. Learn how to automate customer calls with AI." />
        <meta name="keywords" content="AI customer support blog, AI answering service guide, how to automate customer calls with AI, AI call center tips, AI receptionist articles" />
        <link rel="canonical" href="https://call24x7.ai/blog" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/blog" />
        <meta property="og:title" content="AI Customer Support Blog | Tips & Guides" />
        <meta property="og:description" content="Expert insights on AI customer support, AI answering services, and automation strategies." />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Call24x7.AI Blog",
            "description": "Expert insights on AI customer support, AI answering services, and business automation",
            "url": "https://call24x7.ai/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Call24x7.AI",
              "url": "https://call24x7.ai"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <RotatingNewsBanner />
        <SharedHeader />

        <section className="pt-36 pb-12 px-2 sm:px-4">
          <div className="container mx-auto max-w-5xl text-center">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              AI Customer Support Insights
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              AI Customer Support Blog
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Expert guides on AI answering services, customer service automation, and how to reduce support costs with AI technology.
            </p>
          </div>
        </section>

        <section className="py-12 px-2 sm:px-4">
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-2 gap-6">
              {paginatedPosts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg hover:border-primary/50 transition-all group overflow-hidden">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <CardHeader className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {post.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <CardTitle className="text-xl group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="text-base line-clamp-2">
                        {post.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1 hidden sm:flex">
                            <User className="w-4 h-4" />
                            {post.author}
                          </span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={page === currentPage ? "default" : "outline"}
                    size="icon"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="mt-12 text-center p-8 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl">
              <h2 className="text-2xl font-bold mb-2">Ready to Try AI Customer Support?</h2>
              <p className="text-muted-foreground mb-4">
                Build your AI answering service in 60 seconds - no coding required.
              </p>
              <Button size="lg" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                Start Free Trial
              </Button>
            </div>
          </div>
        </section>

        <footer className="border-t py-8 mt-8">
          <div className="container mx-auto px-2 sm:px-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © 2024 Call24x7.AI. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Home
                </Link>
                <Link to="/blog" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Blog
                </Link>
                <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Blog;
